import { cloneDeep } from "lodash"
import {
    patchOccupationCurrentV2,
    patchOwnedPropertyFieldsV2,
    patchParticipantEmploymentV2,
    patchParticipantOtherIncomeV2,
    patchParticipantProfileV2,
    patchParticipantSsnV2,
    patchPresentAddressV2,
    patchPropertyFieldsV2,
    patchSelectionV2,
} from "./applicationV2Client"

const PROFILE_FIELDS = {
    firstname: "firstName",
    middlename: "middleName",
    lastname: "lastName",
    suffix: "suffix",
    email: "email",
    dateofbirth: "dateOfBirth",
    maritalstatus: "maritalStatus",
    hasunmarriedrelationship: "hasUnmarriedRelationship",
    unmarriedrelationship: "unmarriedRelationship",
    otherunmarriedrelationship: "otherUnmarriedRelationship",
    stateofunmarriedrelationship: "stateOfUnmarriedRelationship",
    citizenship: "citizenship",
    visa: "visa",
    visaexpiration: "visaExpiration",
    homephone: "homePhone",
    mobilephone: "mobilePhone",
    workphone: "workPhone",
}

const PRESENT_ADDRESS_FIELDS = {
    presentaddressstreet: "street",
    presentaddressunit: "unit",
    presentaddresscity: "city",
    presentaddresscounty: "county",
    presentaddressstate: "state",
    presentaddresszip: "zip",
    presentaddressyears: "years",
    presentaddressmonths: "months",
}

const MAILING_ADDRESS_FIELDS = {
    mailingaddressstreet: "street",
    mailingaddressunit: "unit",
    mailingaddresscity: "city",
    mailingaddresscounty: "county",
    mailingaddressstate: "state",
    mailingaddresszip: "zip",
}

const OWNED_PROPERTY_FIELDS = {
    address: "address",
    propertytype: "propertytype",
    grossrentalincome: "grossrentalincome",
    insurancemaintenancetaxes: "insurancemaintenancetaxes",
    insurancemain: "insurancemain",
    insurancetax: "insurancetax",
}

const asText = (value) => {
    if (value === undefined || value === null) return ""
    return `${value}`
}

// Two of the employment fields are booleans on the 1003 and every other one is
// a string, so the blanket asText that keeps a number from being sent as one
// sends "true" for these and the save fails outright:
//   json: cannot unmarshal string into Go struct field .occupation.selfemployed
// Nothing had ever written them from a document until the profit and loss
// statement and the business returns started offering selfemployed.
const OCCUPATION_BOOLEAN_FIELDS = new Set(["selfemployed", "employedbyagent"])

const asOccupationValue = (pathParts, value) => {
    const leaf = pathParts[pathParts.length - 1]
    if (!OCCUPATION_BOOLEAN_FIELDS.has(leaf)) return asText(value)
    if (typeof value === "boolean") return value
    const text = `${value ?? ""}`.trim().toLowerCase()
    return text === "true" || text === "yes"
}

const setByPath = (obj, pathParts, value) => {
    let target = obj
    for (let i = 0; i < pathParts.length; i++) {
        const key = pathParts[i]
        if (i === pathParts.length - 1) {
            target[key] = value
            return
        }
        if (!target[key] || typeof target[key] !== "object") {
            target[key] = /^\d+$/.test(pathParts[i + 1]) ? [] : {}
        }
        target = target[key]
    }
}

export const participantToLegacyBorrower = (participant, realEstate) => {
    const p = participant || {}
    const present = p.presentAddress || {}
    const mailing = p.mailingAddress || {}
    const properties = Array.isArray(realEstate?.properties) ? realEstate.properties : []
    return {
        firstname: p.firstName || "",
        middlename: p.middleName || "",
        lastname: p.lastName || "",
        suffix: p.suffix || "",
        email: p.email || "",
        dateofbirth: p.dateOfBirth || "",
        maritalstatus: p.maritalStatus || "",
        citizenship: p.citizenship || "",
        visa: p.visa || "",
        visaexpiration: p.visaExpiration || "",
        homephone: p.homePhone || "",
        mobilephone: p.mobilePhone || "",
        workphone: p.workPhone || "",
        socialsecurity: p.socialSecurity || "",
        presentaddressstreet: present.street || "",
        presentaddressunit: present.unit || "",
        presentaddresscity: present.city || "",
        presentaddresscounty: present.county || "",
        presentaddressstate: present.state || "",
        presentaddresszip: present.zip || "",
        presentaddressyears: present.years || "",
        presentaddressmonths: present.months || "",
        mailingaddressstreet: mailing.street || "",
        mailingaddressunit: mailing.unit || "",
        mailingaddresscity: mailing.city || "",
        mailingaddresscounty: mailing.county || "",
        mailingaddressstate: mailing.state || "",
        mailingaddresszip: mailing.zip || "",
        residenceownership: p.residenceOwnership || "",
        monthlyrent: p.monthlyRent || "",
        occupation: p.occupation || {},
        otheroccupations: p.otheroccupations || [],
        previousoccupations: p.previousoccupations || [],
        incomeorloss: p.occupation?.incomeorloss || "",
        income: p.income || {},
        declarations: {
            servearmedforces: p.serveArmedForces || p.declarations?.servearmedforces || "",
        },
        ownedproperties: properties.map((item) => ({
            address: item.address || item.street || "",
            occupancy: item.occupancy || "",
            status: item.status || "",
            propertytype: item.propertyType || item.propertytype || "",
            grossrentalincome: item.grossRentalIncome || item.grossrentalincome || "",
            insurancemaintenancetaxes: item.insuranceMaintenanceTaxes || item.insurancemaintenancetaxes || "",
            insurancemain: item.insurancemain || "",
            insurancetax: item.insurancetax || "",
            loans: Array.isArray(item.loans) ? item.loans : [],
        })),
    }
}

export const v2ToLegacyApplication = (application, leftPartyId, rightPartyId) => {
    const byId = application?.participantsById || {}
    const realEstate = application?.finance?.realEstateByPartyId || {}
    const left = byId[leftPartyId]
    const right = rightPartyId ? byId[rightPartyId] : null
    return {
        hascoborrower: right ? "withcoborrower" : "",
        borrower: participantToLegacyBorrower(left, realEstate[leftPartyId]),
        coborrower: participantToLegacyBorrower(right, rightPartyId ? realEstate[rightPartyId] : null),
        property: application?.property || {},
        selection: application?.selection || {},
    }
}

const applyPersonPath = (draft, rest, value) => {
    const field = rest[0]
    if (!field) return
    if (field === "socialsecurity") {
        draft.socialSecurity = asText(value)
        draft._dirtySsn = true
        return
    }
    if (PROFILE_FIELDS[field]) {
        draft[PROFILE_FIELDS[field]] = asText(value)
        draft._dirtyProfile = true
        return
    }
    if (PRESENT_ADDRESS_FIELDS[field]) {
        draft.presentAddress = { ...(draft.presentAddress || {}) }
        draft.presentAddress[PRESENT_ADDRESS_FIELDS[field]] = asText(value)
        draft._dirtyResidencePresent = true
        return
    }
    if (MAILING_ADDRESS_FIELDS[field]) {
        draft.mailingAddress = { ...(draft.mailingAddress || {}) }
        draft.mailingAddress[MAILING_ADDRESS_FIELDS[field]] = asText(value)
        draft._dirtyResidenceMailing = true
        return
    }
    if (field === "residenceownership" || field === "residencetype") {
        draft.residenceOwnership = asText(value)
        draft._dirtyResidenceOwnership = true
        return
    }
    if (field === "monthlyrent") {
        draft.monthlyRent = asText(value)
        draft._dirtyResidenceRent = true
        return
    }
    if (field === "incomeorloss") {
        draft.occupation = { ...(draft.occupation || {}), incomeorloss: asText(value) }
        draft._dirtyOccupationCurrent = true
        return
    }
    if (field === "occupation") {
        setByPath(draft, rest, rest.length === 1 ? value : asOccupationValue(rest, value))
        draft._dirtyOccupationCurrent = true
        return
    }
    if (field === "otheroccupations" || field === "previousoccupations") {
        setByPath(draft, rest, rest.length === 1 ? value : asOccupationValue(rest, value))
        draft._dirtyEmploymentLists = true
        return
    }
    if (field === "income") {
        setByPath(draft, rest, asText(value))
        draft._dirtyOtherIncome = true
        return
    }
    if (field === "ownedproperties") {
        const idx = parseInt(rest[1], 10)
        const ownedField = rest[2]
        if (Number.isNaN(idx) || !OWNED_PROPERTY_FIELDS[ownedField]) return
        if (!draft._ownedPropertyFields) draft._ownedPropertyFields = {}
        if (!draft._ownedPropertyFields[idx]) draft._ownedPropertyFields[idx] = {}
        draft._ownedPropertyFields[idx][OWNED_PROPERTY_FIELDS[ownedField]] = asText(value)
        draft._dirtyRealEstate = true
    }
}

const profilePayload = (p) => ({
    firstName: p.firstName,
    middleName: p.middleName,
    lastName: p.lastName,
    suffix: p.suffix,
    email: p.email,
    dateOfBirth: p.dateOfBirth,
    maritalStatus: p.maritalStatus,
    hasUnmarriedRelationship: p.hasUnmarriedRelationship,
    unmarriedRelationship: p.unmarriedRelationship,
    otherUnmarriedRelationship: p.otherUnmarriedRelationship,
    stateOfUnmarriedRelationship: p.stateOfUnmarriedRelationship,
    citizenship: p.citizenship,
    visa: p.visa,
    visaExpiration: p.visaExpiration,
    homePhone: p.homePhone,
    mobilePhone: p.mobilePhone,
    workPhone: p.workPhone,
})

const otherIncomePayload = (p) => ({
    income: p.income || {},
    serveArmedForces: p.serveArmedForces || "",
})

const residenceFieldsPayload = (p) => {
    const payload = {}
    if (p._dirtyResidencePresent) payload.presentAddress = p.presentAddress || {}
    if (p._dirtyResidenceMailing) payload.mailingAddress = p.mailingAddress || {}
    if (p._dirtyResidenceOwnership) payload.residenceOwnership = p.residenceOwnership || ""
    if (p._dirtyResidenceRent) payload.monthlyRent = p.monthlyRent || ""
    return payload
}

const saveParty = async (loanId, partyId, draft) => {
    if (!loanId || !partyId) return
    if (draft._dirtyProfile) {
        await patchParticipantProfileV2(loanId, partyId, profilePayload(draft))
    }
    if (draft._dirtyResidencePresent || draft._dirtyResidenceMailing || draft._dirtyResidenceOwnership || draft._dirtyResidenceRent) {
        await patchPresentAddressV2(loanId, partyId, residenceFieldsPayload(draft))
    }
    if (draft._dirtyEmploymentLists) {
        // Legacy path-write mutates 1c/1d in place. V2 employment PATCH replaces
        // occupation + other + previous together — send the cloned participant tree.
        await patchParticipantEmploymentV2(loanId, partyId, {
            occupation: draft.occupation || {},
            otheroccupations: draft.otheroccupations || [],
            previousoccupations: draft.previousoccupations || [],
        })
    } else if (draft._dirtyOccupationCurrent) {
        await patchOccupationCurrentV2(loanId, partyId, draft.occupation || {})
    }
    if (draft._dirtyOtherIncome) {
        await patchParticipantOtherIncomeV2(loanId, partyId, otherIncomePayload(draft))
    }
    if (draft._dirtySsn) {
        await patchParticipantSsnV2(loanId, partyId, draft.socialSecurity)
    }
    if (draft._dirtyRealEstate && draft._ownedPropertyFields) {
        const indexes = Object.keys(draft._ownedPropertyFields)
        for (const idx of indexes) {
            await patchOwnedPropertyFieldsV2(loanId, partyId, parseInt(idx, 10), draft._ownedPropertyFields[idx])
        }
    }
}

export const applySmartVerifyUpdates = async (loanId, ctx, updates) => {
    if (!loanId || !ctx?.v2Application || !Array.isArray(updates) || updates.length === 0) return
    const app = cloneDeep(ctx.v2Application)
    const byId = app.participantsById || {}
    const leftId = ctx.leftPartyId
    const rightId = ctx.rightPartyId
    const drafts = {}
    const ensureDraft = (partyId) => {
        if (!partyId) return null
        if (!drafts[partyId]) {
            drafts[partyId] = cloneDeep(byId[partyId] || { partyId })
        }
        return drafts[partyId]
    }

    const propertyFields = {}
    const selectionPatch = {}

    updates.forEach(({ path, value }) => {
        if (!path) return
        const parts = `${path}`.split(".")
        const root = parts[0]
        if (root === "borrower") {
            applyPersonPath(ensureDraft(leftId) || {}, parts.slice(1), value)
            return
        }
        if (root === "coborrower") {
            applyPersonPath(ensureDraft(rightId) || {}, parts.slice(1), value)
            return
        }
        if (root === "property") {
            const field = parts[1]
            if (field) propertyFields[field] = asText(value)
            return
        }
        if (root === "selection") {
            const field = parts[1]
            if (field) selectionPatch[field] = asText(value)
        }
    })

    for (const partyId of Object.keys(drafts)) {
        await saveParty(loanId, partyId, drafts[partyId])
    }
    if (Object.keys(propertyFields).length) {
        await patchPropertyFieldsV2(loanId, propertyFields)
    }
    if (Object.keys(selectionPatch).length) {
        await patchSelectionV2(loanId, selectionPatch)
    }
}
